import type { Dispatch, SetStateAction } from 'react'
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider';
import { AgGridReact } from 'ag-grid-react';
import { LoadingOverlay } from 'components/commonparts';
import { useGridEdit } from 'hooks/useGridEdit';
import { useStock } from 'hooks/useStock';
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes';
import { isSameResponseResult } from 'types/types';
import { convertDateToRequestParam, isDateType, isEqualDate } from 'utils/convertFromLocalDate';

import type { CellValueChangedEvent, ColDef, GridApi, IRowNode } from 'ag-grid-community';
import type { ResponseResult, ShipExclusionUpdateBase, Stock, Stocks } from 'api-client';

import 'ag-grid-enterprise'

interface ShipExclusionProps extends DataListPropsBase {
  list: Stocks
}

export interface ShipExclusionHandle {
  addExclusionFromChart: (stock: Stock) => void
}

type StockWithId = ShipExclusionUpdateBase & RecordEditEx

const columnMapping: Record<string, string> = {
  "rec_date": "recDate",
  "ship_exclusion_flg": "shipExclusionFlg",
  "remarks": "remarks",
  "stock_plan_update_datetime": "stockPlanUpdateDatetime" 
}

const primayKey = ['recDate']

/** stock API の同一実績日行から楽観ロック用の更新日時・備考を補完する */
function planUpdateTimeMs(d: Date | null | undefined): number | null {
  if (d == null) return null
  return convertDateToRequestParam(new Date(d)).getTime()
}

function applyChartSelectionViaGridApi(
  api: GridApi,
  stock: Stock,
  createBlankRow: () => StockWithId
): boolean {
  if (stock.recDate == null) return false

  let sameDateNode: IRowNode | undefined
  let blankNode: IRowNode | undefined

  api.forEachNode((node) => {
    const row = node.data as StockWithId | undefined
    if (!row) return
    if (row.recDate != null && isEqualDate(row.recDate, stock.recDate)) {
      sameDateNode = node
    } else if (row.recDate === null) {
      blankNode = node
    }
  })

  if (sameDateNode) {
    const row = sameDateNode.data as StockWithId
    if (row.shipExclusionFlg) return false
    sameDateNode.setData({
      ...row,
      shipExclusionFlg: true,
      stockPlanUpdateDatetime: stock.stockPlanUpdateDatetime
        ? convertDateToRequestParam(new Date(stock.stockPlanUpdateDatetime))
        : null,
      isEdited: true,
      editedFields: new Set(row.editedFields).add('shipExclusionFlg'),
    })
    return true
  }

  if (blankNode) {
    const row = blankNode.data as StockWithId
    blankNode.setData({
      ...row,
      recDate: stock.recDate,
      remarks: stock.remarks,
      shipExclusionFlg: true,
      stockPlanUpdateDatetime: stock.stockPlanUpdateDatetime
        ? convertDateToRequestParam(new Date(stock.stockPlanUpdateDatetime))
        : null,
      isEdited: true,
      editedFields: new Set(['recDate', 'shipExclusionFlg']),
    })
    return true
  }

  const newRow: StockWithId = {
    ...createBlankRow(),
    recDate: stock.recDate,
    remarks: stock.remarks,
    shipExclusionFlg: true,
    stockPlanUpdateDatetime: stock.stockPlanUpdateDatetime
      ? convertDateToRequestParam(new Date(stock.stockPlanUpdateDatetime))
      : null,
    isEdited: true,
    editedFields: new Set(['recDate', 'shipExclusionFlg', 'remarks']),
    errors: {},
  }
  api.applyTransaction({ add: [newRow] })
  return true
}

const ShipExclusion = forwardRef<ShipExclusionHandle, ShipExclusionProps>(({
    list,
    isLoading,
    searchParam,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    onReload,
  },
  ref
) => {
  const { loginUserInfo } = useAuthContext()
  const containerStyle = useMemo(() => ({ width: '70%', height: '100%' }), []);
  const gridStyle = useMemo(() => ({ width: '70%', height: '100%' }), []);
  const gridRef = useRef<AgGridReact<Stock>>(null)
  const checkAndSetErrorRef = useRef<
    ((pRow: StockWithId, field: string, error: string | null) => boolean) | null
  >(null)
  const stockListRef = useRef(list.list)
  stockListRef.current = list.list
  const getRowDataRef = useRef<(() => StockWithId[])>(() => [])
  const [rowData, setRowData] = useState<ShipExclusionUpdateBase[]>()
  const { isUpdating, fetchUpdateShipExclusion, result } = useStock()

  const notifyResultMessage = useCallback<Dispatch<SetStateAction<ResponseResult | undefined>>>(
    (value) => {
      setResultMessage((prev) => {
        const next = typeof value === 'function' ? value(prev) : value
        return isSameResponseResult(prev, next) ? prev : next
      })
    },
    [setResultMessage]
  )

  useEffect(() => {
    if (result) {
      notifyResultMessage(result)
    }
  }, [result, notifyResultMessage])

  const createBlankRow = useCallback((): StockWithId => ({
        companyId: loginUserInfo?.companyId || 0,
        itemCode: searchParam.itemCode,
        centerId: searchParam.centerId,
        recDate: null,
        shipExclusionFlg: true,
        remarks: null,
        stockPlanUpdateDatetime: null,
        ...initRecordEditEx(true)
      }
    ), [loginUserInfo?.companyId, searchParam.centerId, searchParam.itemCode])

  const fieldValidate = (pRow: StockWithId, field: string | undefined, newValue: any): boolean => {
    switch (field) {
      case 'recDate': {
        if (!isDateType(newValue)) return false
        const candidate = newValue as Date
        const api = gridRef.current?.api
        if (!api || api.isDestroyed()) return false

        let duplicate = false
        api.forEachNode((node) => {
          const other = node.data as unknown as StockWithId | undefined
          if (!other?.id || other.id === pRow.id) return
          const rd = other.recDate
          if (rd != null && isEqualDate(rd, candidate)) duplicate = true
        })

        const setErr = checkAndSetErrorRef.current
        if (duplicate && setErr) {
          setErr(pRow, 'recDate', '入力値はすでに登録されています')
          return true
        }
        return false
      }
      default:
        return false
    }
  }

  const mergeShipExclusionRowFromStockList = useCallback(
    (row: StockWithId): StockWithId | null => {
      if (row.recDate == null) return null
      const matching = stockListRef.current.find((item) => isEqualDate(item.recDate, row.recDate))
      if (!matching) return null

      const apiDt = matching.stockPlanUpdateDatetime
        ? convertDateToRequestParam(new Date(matching.stockPlanUpdateDatetime))
        : null
      const prevDt = row.stockPlanUpdateDatetime ?? null
      const dtEqual = planUpdateTimeMs(prevDt) === planUpdateTimeMs(apiDt)

      const apiRemarks = matching.remarks ?? null
      const rowRemarks = row.remarks ?? null
      const emptyRemarks = rowRemarks == null || rowRemarks === ''
      const remarksEditedByUser = row.editedFields.has('remarks')
      const shouldFillRemarks =
        emptyRemarks &&
        apiRemarks != null &&
        apiRemarks !== '' &&
        !remarksEditedByUser
      const nextRemarks = shouldFillRemarks ? apiRemarks : rowRemarks

      if (dtEqual && !shouldFillRemarks) return null

      const editedFields = new Set(row.editedFields)
      if (!dtEqual) editedFields.add('stockPlanUpdateDatetime')

      return {
        ...row,
        stockPlanUpdateDatetime: dtEqual ? row.stockPlanUpdateDatetime : apiDt,
        remarks: nextRemarks,
        isEdited: true,
        editedFields,
      }
    },
    []
  )

  const onShipExclusionCellValueChanged = useCallback(
    (event: CellValueChangedEvent, haserror: boolean) => {
      if (event.colDef.field !== 'recDate') return
      if (haserror) return
      const merged = mergeShipExclusionRowFromStockList(event.data as StockWithId)
      if (merged) {
        event.node.setData(merged)
      }
    },
    [mergeShipExclusionRowFromStockList]
  )

  const updateStock = useCallback(
    async (updatedRows: StockWithId[]) => {
      const gridApi = gridRef.current?.api;
      if (!gridApi) return;
      const datalist = updatedRows.filter(item => item.recDate != null).map(({ isEdited, editedFields, recDate, shipExclusionFlg , errors, ...rest }) => ({
        ...rest,
        companyId: loginUserInfo?.companyId || rest.companyId || 0,
        itemCode: searchParam.itemCode ?? rest.itemCode,
        centerId: searchParam.centerId ?? rest.centerId,
        shipExclusionFlg: shipExclusionFlg == null ? false : shipExclusionFlg,
        recDate: recDate ? convertDateToRequestParam(new Date(recDate)) : null
      }));
      if (datalist.length === 0) return;
      try {
        await fetchUpdateShipExclusion({
          shipExclusionUpdate: { body: datalist }
        })
        if (onReload) onReload()
      } catch (err: any) {
        notifyResultMessage(err.error);
      }
    }, [fetchUpdateShipExclusion, onReload, notifyResultMessage, loginUserInfo?.companyId, searchParam.centerId, searchParam.itemCode]);

  const setHasGridEditedIfChanged = useCallback<Dispatch<SetStateAction<boolean>>>(
    (value) => {
      setHasGridEdited((prev) => {
        const next = typeof value === 'function' ? value(prev) : value
        return prev === next ? prev : next
      })
    },
    [setHasGridEdited]
  )

  const {
    checkAndSetError,
    dateFormatter,
    formatBooleanValue,
    parseBooleanValue,
    parseDateValue,
    localeText,
    onGridReady,
    getCellClassRules,
    getRowData,
    isEnableEdit,
    longTextValueFormatter,
    longTextValueParser,
    longTextEditorParams,
    longTextValueSetter,
    dateTreeSetFilterParams,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    updateTrigger,
    setHasGridEdited: setHasGridEditedIfChanged,
    setResultMessage: notifyResultMessage,
    columnMapping,
    primayKey,
    updateData: updateStock,
    createBlankRow,
    fieldValidate,
    cellValueChanged: onShipExclusionCellValueChanged,
  })

  checkAndSetErrorRef.current = checkAndSetError
  getRowDataRef.current = getRowData as () => StockWithId[]

  const companyIdMismatch = loginUserInfo?.companyId !== searchParam.companyId

  const columnDefs = useMemo<(ColDef)[]>(
    () => [
      {
        headerName: '実績日',
        field: 'recDate',
        editable: (params) => isEnableEdit(params.data && params.data.isNewRecord) && !companyIdMismatch,
        cellClassRules: {
          ...getCellClassRules(),
          'editable-cell': (params) => isEnableEdit(params.data && params.data.recDate === null) && !companyIdMismatch,
        },
        flex: 4,
        cellEditor: 'agDateCellEditor',
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            {filter: 'agDateColumnFilter'},
            {filter: 'agSetColumnFilter',
             filterParams: dateTreeSetFilterParams,
            },
          ],
          closeOnApply: true,
        },
        cellEditorParams: {
          minYear: 1900,
          maxYear: 2050,
        },
        valueFormatter: dateFormatter,
        valueParser: parseDateValue,
      },
      {
        headerName: '計算除外',
        field: 'shipExclusionFlg',
        flex: 2,
        editable: () => isEnableEdit() && !companyIdMismatch,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueFormatter: formatBooleanValue,
        valueParser: parseBooleanValue,
      },
      {
        headerName: '備考',
        field: 'remarks',
        flex: 4,
        editable: () => isEnableEdit() && !companyIdMismatch,
        cellEditorPopup: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorParams: longTextEditorParams,
        valueFormatter: longTextValueFormatter,
        valueParser: longTextValueParser,
        valueSetter: longTextValueSetter,
      },
    ],
    [
      companyIdMismatch,
      dateFormatter,
      dateTreeSetFilterParams,
      formatBooleanValue,
      getCellClassRules,
      isEnableEdit,
      longTextEditorParams,
      longTextValueFormatter,
      longTextValueParser,
      longTextValueSetter,
      parseBooleanValue,
      parseDateValue,
    ]
  )

  const addExclusionFromChart = useCallback(
    (updatedOnPlotSelectDate: Stock) => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      const changed = applyChartSelectionViaGridApi(api, updatedOnPlotSelectDate, createBlankRow)
      if (changed) {
        setHasGridEditedIfChanged(true)
      }
    },
    [createBlankRow, setHasGridEditedIfChanged]
  )

  useImperativeHandle(ref, () => ({ addExclusionFromChart }), [addExclusionFromChart])

  useEffect(() => {
    const filtered = list.list.filter((item) => item.shipExclusionFlg === true)

    const existRow = filtered.map((item) => {
      const blank = createBlankRow()
      blank.recDate = item.recDate
      blank.remarks = item.remarks
      blank.stockPlanUpdateDatetime = item.stockPlanUpdateDatetime ? convertDateToRequestParam(new Date(item.stockPlanUpdateDatetime)) : null
      blank.isNewRecord = false
      return blank
    })

    if (existRow.length === 0) {
      setRowData([createBlankRow()])
    } else {
      setRowData(existRow)
    }
  }, [list.list, createBlankRow])

  useEffect(() => {
    const gridApi = gridRef.current?.api
    if (!gridApi || gridApi.isDestroyed()) return

    const gridRows = getRowDataRef.current() as StockWithId[]
    const manualRows = gridRows.filter((row) => row.isEdited && row.recDate !== null)
    if (manualRows.length === 0) return

    const rowsToUpdate: StockWithId[] = []

    manualRows.forEach((manualRow) => {
      if (manualRow.errors?.recDate?.length) return
      const merged = mergeShipExclusionRowFromStockList(manualRow)
      if (merged) {
        rowsToUpdate.push(merged)
      }
    })

    if (rowsToUpdate.length > 0) {
      gridApi.applyTransaction({ update: rowsToUpdate as unknown as Stock[] })
    }
  }, [list.list, mergeShipExclusionRowFromStockList])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '25vh' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={rowData}
            ref={gridRef}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            stopEditingWhenCellsLoseFocus
            animateRows={false}
            getRowId={(params) => String((params.data as StockWithId | undefined)?.id ?? '')}
          />
        </div>
      </div>
    </div>
  )
})

export default memo(ShipExclusion)
